import api from '../api'
import { buildKeyPayloadFromForm, createBlankKeyForm } from '@/composables/buildKeyPayload'
import { mergeTags } from '@/composables/useSearchKeyQuiz'
import { isLegacyGlobalKey } from '@/utils/setupGate'
import { formatQuizApiError, QUIZ_API_TIMEOUT_MS } from '@/utils/quizApiErrors'

export function collectQuizAnswers(quizItems, quizDraft) {
  return quizItems.map((item) => ({
    question: item.question,
    answer: String(quizDraft[item.id] ?? '').trim(),
  }))
}

export function validateQuizRequired(quizItems, quizDraft) {
  return quizItems.filter((item) => !item.optional && !String(quizDraft[item.id] ?? '').trim())
}

export function quizErrorMessage(error, fallback = 'Не удалось сгенерировать настройки профиля.') {
  return formatQuizApiError(error, fallback)
}

export async function fetchSetupFromQuiz(answers, { skipCriteria = false, skipKey = false } = {}) {
  const { data } = await api.post(
    '/setup/from-quiz/',
    {
      answers,
      skip_criteria: skipCriteria,
      skip_key: skipKey,
    },
    { timeout: QUIZ_API_TIMEOUT_MS },
  )
  return data
}

export async function saveCriteriaFromAiList(criteria, { onSaveProgress } = {}) {
  const list = Array.isArray(criteria) ? criteria : []
  const toSave = list
    .map((c) => ({
      name: String(c.name || '').trim(),
      description: String(c.description || '').trim(),
      criticality_percent: Math.max(0, Math.min(100, Number(c.criticality_percent) || 0)),
    }))
    .filter((p) => p.name)

  if (!list.length) {
    return { ok: false, error: 'Сервис вернул пустой список критериев.' }
  }
  if (!toSave.length) {
    return { ok: false, error: 'В ответе нет ни одного критерия с непустым названием.' }
  }

  let done = 0
  const total = toSave.length
  for (const payload of toSave) {
    await api.post('/win-criteria/', payload)
    done += 1
    onSaveProgress?.({ done, total })
  }
  return { ok: true, savedCount: toSave.length }
}

function applyKeyFormOverlay(form, overlay) {
  if (!overlay || typeof overlay !== 'object') return
  if (Array.isArray(overlay.regions)) form.regions = [...overlay.regions]
  if (overlay.minPrice !== undefined && overlay.minPrice !== null) form.minPrice = overlay.minPrice
  if (overlay.maxPrice !== undefined && overlay.maxPrice !== null) form.maxPrice = overlay.maxPrice
  if (overlay.prepayment !== undefined && overlay.prepayment !== null) form.prepayment = overlay.prepayment
  if (overlay.guaranteeApp) form.guaranteeApp = overlay.guaranteeApp
  if (overlay.guaranteeAppMax !== undefined && overlay.guaranteeAppMax !== null) {
    form.guaranteeAppMax = overlay.guaranteeAppMax
  }
  if (overlay.guaranteeContract) form.guaranteeContract = overlay.guaranteeContract
  if (overlay.guaranteeContractMax !== undefined && overlay.guaranteeContractMax !== null) {
    form.guaranteeContractMax = overlay.guaranteeContractMax
  }
  if (overlay.flags && typeof overlay.flags === 'object') {
    Object.assign(form.flags, overlay.flags)
  }
  const overlayName = String(overlay.name || '').trim()
  if (overlayName) form.name = overlayName
}

/** Персональный ключ: POST /keys/. Глобальный legacy-ключ не PATCH. */
export async function saveKeyFromAiResult(aiKey, primaryKey, keyForm = null) {
  const words = Array.isArray(aiKey.words) ? aiKey.words : []
  const excluded = Array.isArray(aiKey.excluded) ? aiKey.excluded : []
  if (!words.length && !excluded.length) {
    return { ok: false, error: 'Сервис не вернул ключевые слова или исключения.' }
  }

  const form = createBlankKeyForm()
  applyKeyFormOverlay(form, keyForm)
  form.words = mergeTags(form.words, words)
  form.docWords = mergeTags(form.docWords, excluded)
  const suggestedName = String(aiKey.name || '').trim()
  if (suggestedName) {
    form.name = suggestedName
  } else if (!form.name.trim()) {
    form.name = 'Мой поиск'
  }

  const built = buildKeyPayloadFromForm(form)
  if (built.error) {
    return { ok: false, error: built.error }
  }

  const keyId = primaryKey?._id ?? primaryKey?.id
  const canPatch = keyId && !isLegacyGlobalKey(keyId) && !isLegacyGlobalKey(primaryKey)

  if (canPatch) {
    await api.patch(`/keys/${keyId}/`, built.payload)
    return { ok: true, keyId: String(keyId) }
  }

  const { data } = await api.post('/keys/', built.payload)
  const createdId = String(data?._id ?? data?.id ?? '')
  return { ok: true, keyId: createdId, created: true }
}